import React, { useState, useEffect, useRef } from 'react';
import { obtenerMesas, liberarMesa, asignarGarzonMesa, guardarOrdenMesas, guardarPosicionesMesas, actualizarMesa } from '../../services/mesasServicio';
import { toast, ToastContainer } from 'react-toastify';
import config from '../../config/apiConfig';
import CatalogoMesas from './CatalogoMesas';

const INTERVALO_REFRESCO = 10000;

// El garzon se guarda POR MESA, nunca global: cada mesa libre pregunta y
// cada mesa con cuenta en curso entra directo con el garzon que le
// corresponde. La clave guarda {nombre} (elegido) o {sin:true} (se decidio
// continuar sin asignar); si no existe, nunca se pregunto por esa mesa.
const CLAVE_MESA = (mesaId) => `garzon_mesa_${mesaId}`;

const formatCLP = (valor) => Number(valor || 0).toLocaleString('es-CL', {
    style: 'currency', currency: 'CLP', minimumFractionDigits: 0
});

// Hora de apertura de la cuenta que se muestra en el tile de la mesa.
const formatHoraMesa = (fecha) => {
    if (!fecha) return '';
    // Flask serializa los datetime como "Thu, 01 Oct 2026 09:33:42 GMT";
    // también aceptamos ISO con espacio. Probar la fecha tal cual primero.
    let d = new Date(fecha);
    if (isNaN(d.getTime()) && typeof fecha === 'string') {
        d = new Date(fecha.replace(' ', 'T'));
    }
    if (isNaN(d.getTime())) return '';
    return d.toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit', hour12: false });
};

// ===== MAPA DE SALON =====
// La cuadricula NO se dibuja: es solo una referencia interna (columna/fila)
// de en que celda queda cada mesa.
const COLUMNAS_MAPA = 6;  // columnas de referencia del mapa
const ALTO_CELDA = 150;   // alto de celda en px (debe calzar con el puntero)

// Celda efectiva de una mesa. Si nunca se le asigno, se deriva de `orden`
// para que ninguna mesa se pierda ni se solape al hacer el primer mapa.
const celdaDeMesa = (mesa, indice) => {
    const x = Number(mesa.pos_x);
    const y = Number(mesa.pos_y);
    if (Number.isFinite(x) && Number.isFinite(y) && x > 0 && y > 0) {
        return { x: x, y: y };
    }
    const base = Number(mesa.orden) > 0 ? Number(mesa.orden) : (indice + 1);
    return {
        x: ((base - 1) % COLUMNAS_MAPA) + 1,
        y: Math.floor((base - 1) / COLUMNAS_MAPA) + 1
    };
};

const leerGarzonMesa = (mesaId) => {
    try {
        const raw = localStorage.getItem(CLAVE_MESA(mesaId));
        if (raw === null || raw === undefined) return undefined;
        return JSON.parse(raw);
    } catch (e) {
        return undefined;
    }
};

const guardarGarzonMesa = (mesaId, miembro) => {
    try {
        if (miembro && miembro.nombre) {
            localStorage.setItem(CLAVE_MESA(mesaId), JSON.stringify({ nombre: miembro.nombre }));
        } else {
            localStorage.setItem(CLAVE_MESA(mesaId), JSON.stringify({ sin: true }));
        }
    } catch (e) {}
};

const limpiarGarzonMesa = (mesaId) => {
    try { localStorage.removeItem(CLAVE_MESA(mesaId)); } catch (e) {}
};

const PedidosMesa = () => {
    const [mesas, setMesas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [mesaActiva, setMesaActiva] = useState(null);
    const [zonaSeleccionada, setZonaSeleccionada] = useState(null); // null = Todas
    const [garzonActivo, setGarzonActivo] = useState(null);
    const [staff, setStaff] = useState([]);
    const [mesaPendiente, setMesaPendiente] = useState(null); // mesa esperando selección de garzón

    // Modo orden manual de la grilla (mapeo de mesas)
    const [ordenando, setOrdenando] = useState(false);
    const [ordenLocal, setOrdenLocal] = useState([]); // mesas en el orden en que se están pintando
    const [guardandoOrden, setGuardandoOrden] = useState(false);
    const [mesaArrastrada, setMesaArrastrada] = useState(null);
    const [mesaArrastradaId, setMesaArrastradaId] = useState(null);

    // Reloj flotante (solo la hora local de la terminal)
    const [reloj, setReloj] = useState(new Date());

    // Celda a la que apunta el puntero mientras se arrastra una mesa
    const [celdaApuntada, setCeldaApuntada] = useState(null);
    const mapaRef = useRef(null);

    useEffect(() => {
        const t = setInterval(() => setReloj(new Date()), 1000);
        return () => clearInterval(t);
    }, []);

    const terminalNombre = localStorage.getItem('terminalNombre');

    useEffect(() => {
        cargarMesas();
        cargarStaff();
        const intervalo = setInterval(() => {
            // Mientras se reordena no refrescamos: re-pintaría la grilla a mitad de edición.
            if (!mesaActiva && !ordenando) cargarMesas();
        }, INTERVALO_REFRESCO);
        return () => clearInterval(intervalo);
    }, [mesaActiva, ordenando]);

    // Al volver a la pestaña (cobro en otra terminal, cambio de turno) refrescar
    // de inmediato en vez de esperar hasta 10 s.
    useEffect(() => {
        const refrescar = () => {
            if (!mesaActiva && !ordenando) cargarMesas();
        };
        const alVolverVisible = () => {
            if (document.visibilityState === 'visible') refrescar();
        };
        window.addEventListener('focus', refrescar);
        document.addEventListener('visibilitychange', alVolverVisible);
        return () => {
            window.removeEventListener('focus', refrescar);
            document.removeEventListener('visibilitychange', alVolverVisible);
        };
    }, [mesaActiva, ordenando]);

    const cargarStaff = async () => {
        try {
            const res = await fetch(`${config.api_url}/api/staff`, {
                headers: { 'Content-Type': 'application/json', 'token': localStorage.getItem('token') }
            });
            const json = await res.json();
            setStaff(json.status === 'success' ? (json.data || []) : []);
        } catch {}
    };

    const abrirSelectorGarzon = (mesa) => {
        const pedidoEnCurso = Boolean(mesa.estado && mesa.estado !== 'LIBRE');
        const decidido = leerGarzonMesa(mesa.id);

        // MESA CON CUENTA EN CURSO: ya entra directo con el garzon de ESA
        // mesa (el de la BD, o el elegido en esta sesion). No se pregunta.
        if (pedidoEnCurso) {
            if (mesa.garzon_nombre) {
                setGarzonActivo({ nombre: mesa.garzon_nombre });
            } else if (decidido && decidido.nombre) {
                // La BD no quedo con el nombre (ej: fallo de red al asignar),
                // pero se eligio en esta sesion: se usa el respaldo local.
                setGarzonActivo({ nombre: decidido.nombre });
            } else if (decidido && decidido.sin) {
                setGarzonActivo(null);
            } else if (staff.length > 0) {
                // Cuenta en curso que nunca pregunto garzon: se pregunta una vez.
                setGarzonActivo(null);
                setMesaPendiente(mesa);
                return;
            } else {
                setGarzonActivo(null);
            }
            setMesaActiva(mesa);
            return;
        }

        // MESA LIBRE (cuenta nueva): SIEMPRE se pregunta el garzon, aunque
        // hace un momento se haya elegido uno en otra mesa. Cada mesa
        // guarda lo suyo.
        if (staff.length > 0) {
            setGarzonActivo(null);
            setMesaPendiente(mesa);
        } else {
            setMesaActiva(mesa);
        }
    };

    // Cambiar el garzon de la mesa desde dentro del pedido.
    const cambiarGarzon = () => {
        const mesa = mesaActiva;
        // Se borra la decision de ESA mesa para que vuelva a preguntar.
        limpiarGarzonMesa(mesa.id);
        setMesaActiva(null);
        setGarzonActivo(null);
        setMesaPendiente(mesa);
    };

    const seleccionarGarzonYAbrir = (miembro) => {
        const mesa = mesaPendiente;
        setGarzonActivo(miembro || null);
        // Guardado POR MESA: local (respaldo al toque) + BD (lo persiste para
        // las proximas veces que se abra esta misma mesa).
        guardarGarzonMesa(mesa.id, miembro || null);
        setMesaPendiente(null);
        setMesaActiva(mesa);

        // La BD es la fuente principal; si falla, no bloquea la apertura:
        // el pedido igual se toma con el garzon de esta sesion y queda el
        // respaldo local.
        asignarGarzonMesa(mesa.id, miembro ? miembro.nombre : null).catch(() => {});
    };

    const cargarMesas = async () => {
        try {
            const data = await obtenerMesas();
            setMesas(Array.isArray(data) ? data : []);
        } catch (error) {
            toast.error("Error al cargar mesas.");
        } finally {
            setLoading(false);
        }
    };

    const volverYRefrescar = () => {
        setMesaActiva(null);
        cargarMesas();
    };

    // =================== ORDEN MANUAL DE LA GRILLA ===================
    const iniciarOrden = () => {
        setZonaSeleccionada(null);
        setOrdenLocal(mesas);
        setOrdenando(true);
    };

    const cancelarOrden = () => {
        setOrdenando(false);
        setOrdenLocal([]);
        setMesaArrastrada(null);
    };

    const guardarOrden = async () => {
        if (ordenLocal.length === 0) return;
        // Confirmación: se van a cambiar posición y/o zona de las mesas.
        const cambiosZona = ordenLocal.filter(m => {
            const orig = mesas.find(x => x.id === m.id);
            return orig && String(orig.zona_id) !== String(m.zona_id);
        }).length;
        const cambiosPos = ordenLocal.filter(m => {
            const orig = mesas.find(x => x.id === m.id);
            if (!orig) return false;
            const a = celdaDeMesa(orig, mesas.indexOf(orig));
            const b = celdaDeMesa(m, ordenLocal.indexOf(m));
            return a.x !== b.x || a.y !== b.y;
        }).length;

        const partes = [];
        if (cambiosPos > 0) partes.push(`${cambiosPos} mesa(s) cambiadas de lugar`);
        if (cambiosZona > 0) partes.push(`${cambiosZona} mesa(s) movidas de zona`);
        const msg = partes.length > 0
            ? `¿Guardar la nueva distribución? Se actualizará: ${partes.join(' y ')}.`
            : "¿Guardar la nueva distribución de mesas?";
        if (!window.confirm(msg)) return;

        setGuardandoOrden(true);
        try {
            // 1. Persistir cambios de zona (mesas que se movieron a otra zona).
            for (const m of ordenLocal) {
                const orig = mesas.find(x => x.id === m.id);
                if (orig && String(orig.zona_id) !== String(m.zona_id)) {
                    await actualizarMesa(m.id, m.nombre, m.zona_id || null);
                }
            }
            // 2. Persistir la celda de cada mesa en el mapa.
            await guardarPosicionesMesas(ordenLocal.map((m, i) => {
                const c = celdaDeMesa(m, i);
                return { id: m.id, pos_x: c.x, pos_y: c.y };
            }));
            // 3. Orden de respaldo (por si la BD no tiene pos_x/pos_y aun).
            await guardarOrdenMesas(ordenLocal.map((m, i) => ({ id: m.id, orden: i + 1 })));
            toast.success("Distribución de mesas guardada");
            setOrdenando(false);
            setOrdenLocal([]);
            await cargarMesas();
        } catch (error) {
            toast.error("No se pudo guardar la distribución de mesas.");
        } finally {
            setGuardandoOrden(false);
        }
    };

    // A que celda de la cuadricula apunta el puntero (la grilla no se pinta,
    // pero se calcula igual por coordenadas).
    const celdaDesdePointer = (clientX, clientY) => {
        const el = mapaRef.current;
        if (!el) return null;
        const r = el.getBoundingClientRect();
        if (r.width <= 0) return null;
        const anchoCelda = r.width / COLUMNAS_MAPA;
        const x = Math.min(
            Math.max(Math.floor((clientX - r.left) / anchoCelda) + 1, 1),
            COLUMNAS_MAPA
        );
        const y = Math.max(Math.floor((clientY - r.top) / ALTO_CELDA) + 1, 1);
        return { x: x, y: y };
    };

    const moverSobreMapa = (e) => {
        if (!ordenando || !mesaArrastradaId) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        const celda = celdaDesdePointer(e.clientX, e.clientY);
        if (!celda) return;
        setCeldaApuntada(prev => (prev && prev.x === celda.x && prev.y === celda.y) ? prev : celda);
    };

    // Suelta en la celda: si hay otra mesa ahi, se intercambian.
    const soltarEnCelda = (e) => {
        if (!ordenando || !mesaArrastradaId) return;
        e.preventDefault();
        const destino = celdaDesdePointer(e.clientX, e.clientY);
        const idArrastrada = mesaArrastradaId;
        setCeldaApuntada(null);
        if (!destino) return;

        setOrdenLocal(prev => {
            const conCeldas = prev.map((m, i) => ({ mesa: m, celda: celdaDeMesa(m, i) }));
            const origen = conCeldas.find(o => String(o.mesa.id) === String(idArrastrada));
            if (!origen) return prev;

            const destinoOcupado = conCeldas.find(o =>
                String(o.mesa.id) !== String(idArrastrada) &&
                o.celda.x === destino.x && o.celda.y === destino.y
            );

            return prev.map((m, i) => {
                const celda = celdaDeMesa(m, i);
                if (String(m.id) === String(idArrastrada)) {
                    return { ...m, pos_x: destino.x, pos_y: destino.y };
                }
                // Si habia otra mesa en la celda, se queda en el lugar de origen
                if (destinoOcupado && String(m.id) === String(destinoOcupado.mesa.id)) {
                    return { ...m, pos_x: origen.celda.x, pos_y: origen.celda.y };
                }
                return m;
            });
        });
    };

    const salirDelMapa = () => setCeldaApuntada(null);

    // Cambia la zona de una mesa mientras se edita el mapa.
    const cambiarZonaDeMesa = (mesaId, nuevaZonaId) => {
        const zona = zonas.find(z => String(z.id) === String(nuevaZonaId));
        setOrdenLocal(prev => prev.map(m =>
            m.id === mesaId
                ? { ...m, zona_id: nuevaZonaId ? Number(nuevaZonaId) : null, zona_nombre: zona ? zona.nombre : null }
                : m
        ));
    };

    const cambiarZona = (zonaId) => {
        setZonaSeleccionada(zonaId);
        // Si estamos reordenando, la lista en edición pasa a ser la de la nueva zona.
        if (ordenando) {
            setOrdenLocal(zonaId === null ? mesas : mesas.filter(m => m.zona_id === zonaId));
        }
    };

    const handleCancelarMesa = async (mesa, e) => {
        e.stopPropagation();
        if (!window.confirm(`¿Estás seguro de liberar la ${mesa.nombre}?`)) return;
        try {
            await liberarMesa(mesa.id);
            toast.success(`La ${mesa.nombre} ha sido liberada.`);
            cargarMesas();
        } catch (error) {
            toast.error("Error al intentar liberar la mesa.");
        }
    };

    if (mesaActiva) {
        return <CatalogoMesas key={mesaActiva.id} mesa={mesaActiva} onVolver={volverYRefrescar} garzon={garzonActivo} onCambiarGarzon={cambiarGarzon} />;
    }

    // Modal de selección de garzón
    if (mesaPendiente) {
        return (
            <div style={{ backgroundColor: '#f0f2f5', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="card shadow" style={{ maxWidth: 420, width: '100%', borderRadius: 16, padding: '24px' }}>
                    <h5 className="fw-bold mb-1">¿Quién atiende la {mesaPendiente.nombre}?</h5>
                    <p className="text-muted small mb-4">Selecciona el garzón o continúa sin asignar.</p>
                    <div className="d-flex flex-wrap" style={{ gap: 10 }}>
                        {staff.length === 0 && (
                            <p className="text-muted text-center w-100">Sin personal configurado — usa "Continuar sin asignar"</p>
                        )}
                        {staff.map(m => (
                            <button key={m.uuid || m.id} className="btn btn-outline-dark fw-bold px-4 py-2" style={{ borderRadius: 10 }}
                                onClick={() => seleccionarGarzonYAbrir(m)}>
                                {m.nombre}
                            </button>
                        ))}
                    </div>
                    <div className="d-flex justify-content-between align-items-center mt-4">
                        <button onClick={() => seleccionarGarzonYAbrir(null)} className="btn btn-link text-muted" style={{ fontSize: '0.85rem' }}>
                            Continuar sin asignar garzón
                        </button>
                        <button onClick={() => setMesaPendiente(null)} className="btn btn-light btn-sm">
                            Cancelar
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Zonas únicas presentes en las mesas recibidas del backend
    const zonas = [...new Map(
        mesas
            .filter(m => m.zona_id && m.zona_nombre)
            .map(m => [m.zona_id, { id: m.zona_id, nombre: m.zona_nombre }])
    ).values()];

    const mesasFiltradas = zonaSeleccionada === null
        ? mesas
        : mesas.filter(m => m.zona_id === zonaSeleccionada);

    // En modo orden la grilla pinta la lista en edición, no la del servidor.
    const listaGrilla = ordenando ? ordenLocal : mesasFiltradas;

    // Filas de referencia del mapa (la cuadricula no se dibuja, solo se da alto
    // suficiente para poder soltar mesas en filas nuevas).
    const filasMapa = listaGrilla.reduce(
        (max, m, i) => Math.max(max, celdaDeMesa(m, i).y), 1
    ) + (ordenando ? 2 : 0);

    return (
        <div style={{ backgroundColor: '#f0f2f5', minHeight: '100vh', paddingBottom: '80px' }}>
            <ToastContainer position="top-right" autoClose={2500} />

            {/* HEADER */}
            <div className="bg-white border-bottom shadow-sm px-4 py-3 sticky-top" style={{ zIndex: 100 }}>
                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div>
                        <h2 className="h5 fw-bold mb-0 text-dark">
                            <i className="fas fa-th-large text-dark mr-2"></i> Gestor de Pedidos
                        </h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '2px' }}>
                            <small className="text-muted">{listaGrilla.length} mesa{listaGrilla.length !== 1 ? 's' : ''}</small>
                            {terminalNombre && (
                                <>
                                    <span style={{ width: '1px', height: '10px', background: '#dee2e6', display: 'inline-block' }}></span>
                                    <span
                                        title="Punto de venta — cambia desde el menú de usuario"
                                        style={{
                                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                                            fontSize: '0.7rem', fontWeight: '600', color: '#f26b21'
                                        }}
                                    >
                                        <i className="fas fa-desktop" style={{ fontSize: '9px' }}></i>
                                        {terminalNombre}
                                    </span>
                                </>
                            )}
                        </div>
                    </div>
                    <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                        {!ordenando ? (
                            <button
                                className="btn btn-outline-dark btn-sm rounded-pill px-3 fw-bold shadow-sm"
                                onClick={iniciarOrden}
                                disabled={loading || mesas.length === 0}
                                title="Editar distribución de mesas (drag & drop)"
                                style={{ fontSize: '0.85rem' }}
                            >
                                <i className="fas fa-edit mr-1"></i> Editar mapa
                            </button>
                        ) : (
                            <>
                                <button
                                    className="btn btn-success btn-sm rounded-pill px-3 fw-bold shadow-sm"
                                    onClick={guardarOrden}
                                    disabled={guardandoOrden}
                                    style={{ fontSize: '0.85rem' }}
                                >
                                    {guardandoOrden
                                        ? <><i className="fas fa-spinner fa-spin mr-1"></i> Guardando...</>
                                        : <><i className="fas fa-check mr-1"></i> Guardar</>}
                                </button>
                                <button
                                    className="btn btn-light btn-sm rounded-pill px-3 fw-bold border"
                                    onClick={cancelarOrden}
                                    disabled={guardandoOrden}
                                    style={{ fontSize: '0.85rem' }}
                                >
                                    Cancelar
                                </button>
                            </>
                        )}
                        <button className="btn btn-dark btn-sm rounded-pill px-4 fw-bold shadow-sm" onClick={cargarMesas} disabled={loading || ordenando} style={{ fontSize: '0.85rem' }}>
                            {loading ? <><i className="fas fa-spinner fa-spin mr-1"></i> Actualizando...</> : <><i className="fas fa-sync-alt mr-1"></i> Actualizar</>}
                        </button>
                    </div>
                </div>

                {/* Aviso del modo orden */}
                {ordenando && (
                    <div className="d-flex align-items-center mt-2 px-2 py-1" style={{ backgroundColor: '#fff3cd', border: '1px solid #ffe08a', borderRadius: '8px', gap: '6px' }}>
                        <i className="fas fa-grip-lines text-warning"></i>
                        <small className="text-dark fw-bold" style={{ fontSize: '0.78rem' }}>
                            Modo edición: arrastra cada mesa al lugar que quieras en el plano (si choca con otra, se intercambian). Luego pulsa Guardar.
                        </small>
                    </div>
                )}

                {/* Tabs de zonas — solo se muestran si hay al menos una zona definida */}
                {zonas.length > 0 && (
                    <div className="d-flex flex-wrap mt-2" style={{ gap: '6px' }}>
                        <button
                            onClick={() => cambiarZona(null)}
                            onDragOver={(e) => { if (ordenando) e.preventDefault(); }}
                            onDrop={(e) => {
                                if (!ordenando || !mesaArrastradaId) return;
                                e.preventDefault();
                                cambiarZonaDeMesa(mesaArrastradaId, null);
                            }}
                            style={{
                                border: 'none', borderRadius: '99px', padding: '5px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                                backgroundColor: zonaSeleccionada === null ? '#111' : '#f0f0f0',
                                color: zonaSeleccionada === null ? '#fff' : '#555',
                                transition: 'all 0.15s'
                            }}
                        >
                            Todas
                        </button>
                        {zonas.map(z => (
                            <button
                                key={z.id}
                                onClick={() => cambiarZona(z.id)}
                                onDragOver={(e) => { if (ordenando) e.preventDefault(); }}
                                onDrop={(e) => {
                                    if (!ordenando || !mesaArrastradaId) return;
                                    e.preventDefault();
                                    cambiarZonaDeMesa(mesaArrastradaId, z.id);
                                }}
                                style={{
                                    border: 'none', borderRadius: '99px', padding: '5px 14px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                                    backgroundColor: zonaSeleccionada === z.id ? '#111' : '#f0f0f0',
                                    color: zonaSeleccionada === z.id ? '#fff' : '#555',
                                    transition: 'all 0.15s'
                                }}
                            >
                                {z.nombre}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            <div className="container mt-4">
                {/* Leyenda */}
                <div className="d-flex justify-content-center flex-wrap mb-4" style={{ gap: '10px' }}>
                    <div className="small"><i className="fas fa-circle text-success"></i> Libre</div>
                    <div className="small"><i className="fas fa-circle text-danger"></i> Ocupada</div>
                    <div className="small"><i className="fas fa-circle text-warning"></i> Por cobrar</div>
                </div>

                {loading ? (
                    <div className="text-center mt-5"><div className="spinner-border text-primary"></div></div>
                ) : (
                    <div
                        ref={mapaRef}
                        style={{
                            display: 'grid',
                            gridTemplateColumns: `repeat(${COLUMNAS_MAPA}, minmax(90px, 1fr))`,
                            gridAutoRows: `${ALTO_CELDA}px`,
                            minHeight: `${filasMapa * ALTO_CELDA}px`,
                            gap: 0
                        }}
                        onDragOver={moverSobreMapa}
                        onDrop={soltarEnCelda}
                    >
                        {/* Resalte de la celda destino — solo mientras se arrastra.
                            La cuadricula normal no se dibuja: es una referencia interna. */}
                        {ordenando && celdaApuntada && (
                            <div style={{
                                gridColumn: celdaApuntada.x,
                                gridRow: celdaApuntada.y,
                                border: '2px dashed rgba(249,115,22,0.8)',
                                borderRadius: '50%',
                                background: 'rgba(249,115,22,0.10)',
                                margin: '14px',
                                pointerEvents: 'none',
                                zIndex: 5
                            }} />
                        )}
                        {listaGrilla.map((mesa, idx) => {
                            const celda = celdaDeMesa(mesa, idx);
                            const esLibre = mesa.estado === 'LIBRE';
                            const esPorCobrar = mesa.estado === 'PENDIENTE' || Number(mesa.estado_id) === 7;

                            let bgColor = '#ffffff';
                            let iconColor = '#6c757d';
                            let borderColor = '#dee2e6';
                            let statusIcon = 'fa-chair';

                            if (!esLibre) {
                                bgColor = '#ff4d4d';
                                iconColor = '#fff';
                                borderColor = '#ff4d4d';
                                statusIcon = 'fa-users';
                            }
                            if (esPorCobrar) {
                                bgColor = '#ffc107';
                                iconColor = '#000';
                                borderColor = '#ffc107';
                                statusIcon = 'fa-file-invoice-dollar';
                            }
                            if (esLibre) {
                                bgColor = '#2ecc71';
                                iconColor = '#fff';
                                borderColor = '#2ecc71';
                            }

                            return (
                                <div
                                    key={mesa.id}
                                    draggable={ordenando}
                                    onDragStart={(e) => {
                                        setMesaArrastrada(idx);
                                        setMesaArrastradaId(mesa.id);
                                        // Sin dataTransfer algunos navegadores no inician el arrastre
                                        e.dataTransfer.effectAllowed = 'move';
                                        e.dataTransfer.setData('text/plain', String(mesa.id));
                                        e.dataTransfer.setData('application/x-mesa', String(mesa.id));
                                    }}
                                    onDragEnd={() => {
                                        setMesaArrastrada(null);
                                        setMesaArrastradaId(null);
                                        setCeldaApuntada(null);
                                    }}
                                    style={{
                                        gridColumn: celda.x,
                                        gridRow: celda.y,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: ordenando ? 'grab' : 'default',
                                        opacity: mesaArrastrada === idx ? 0.35 : 1,
                                        padding: '8px 4px',
                                        zIndex: 6
                                    }}
                                >
                                    <div style={{ position: 'relative', width: '100px' }}>

                                        <button
                                            className="btn shadow-sm mesa-circular"
                                            onClick={() => { if (!ordenando) abrirSelectorGarzon(mesa); }}
                                            title={ordenando ? `Posición ${idx + 1}` : undefined}
                                            style={{
                                                backgroundColor: bgColor,
                                                border: `3px solid ${borderColor}`,
                                                borderRadius: '50%',
                                                width: '90px',
                                                height: '90px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                transition: 'transform 0.2s, box-shadow 0.2s',
                                                padding: 0,
                                                position: 'relative',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <i className={`fas ${statusIcon}`} style={{ fontSize: '20px', color: iconColor }}></i>
                                            <span style={{
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                color: iconColor,
                                                lineHeight: '1.1',
                                                marginTop: '2px'
                                            }}>
                                                {mesa.nombre.replace('Mesa ', '')}
                                            </span>
                                        </button>

                                        {/* Badge garzón asignado */}
                                        {!esLibre && mesa.garzon_nombre && (
                                            <div title={mesa.garzon_nombre} style={{
                                                position: 'absolute', bottom: '-2px', left: '50%',
                                                transform: 'translateX(-50%)',
                                                backgroundColor: '#343a40', color: 'white',
                                                borderRadius: '99px', padding: '2px 8px',
                                                fontSize: '10px', fontWeight: '600',
                                                whiteSpace: 'nowrap', border: '2px solid white', zIndex: 10
                                            }}>
                                                {mesa.garzon_nombre.split(' ')[0]}
                                            </div>
                                        )}

                                        {/* Botón X de liberación rápida */}
                                        {!esLibre && !ordenando && (
                                            <div
                                                onClick={(e) => handleCancelarMesa(mesa, e)}
                                                style={{
                                                    position: 'absolute',
                                                    top: '-2px',
                                                    right: '-2px',
                                                    backgroundColor: '#343a40',
                                                    color: 'white',
                                                    borderRadius: '50%',
                                                    width: '24px',
                                                    height: '24px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '10px',
                                                    cursor: 'pointer',
                                                    zIndex: 10,
                                                    border: '2px solid white'
                                                }}
                                            >
                                                <i className="fas fa-times"></i>
                                            </div>
                                        )}

                                        {/* En modo edición solo se arrastra; sin botones extra */}
                                    </div>

                                    {/* Total y hora de apertura de la mesa (solo si tiene cuenta) */}
                                    {!esLibre && (Number(mesa.pedido_total) > 0 || formatHoraMesa(mesa.fecha_creacion)) && (
                                        <div style={{ marginTop: '6px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                            {Number(mesa.pedido_total) > 0 && (
                                                <div style={{
                                                    backgroundColor: '#111', color: '#fff',
                                                    borderRadius: '99px', padding: '3px 10px', fontSize: '12px',
                                                    fontWeight: 800, whiteSpace: 'nowrap', boxShadow: '0 3px 8px rgba(0,0,0,.15)'
                                                }}>
                                                    {formatCLP(mesa.pedido_total)}
                                                </div>
                                            )}
                                            {formatHoraMesa(mesa.fecha_creacion) && (
                                                <div style={{ fontSize: '10px', fontWeight: 700, color: '#6c757d', whiteSpace: 'nowrap' }}>
                                                    <i className="far fa-clock" style={{ marginRight: '3px' }}></i>
                                                    {formatHoraMesa(mesa.fecha_creacion)}
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Selector de zona (solo en modo edición) */}
                                    {ordenando && zonas.length > 0 && (
                                        <select
                                            value={mesa.zona_id || ''}
                                            onChange={(e) => cambiarZonaDeMesa(mesa.id, e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            style={{
                                                marginTop: '6px', fontSize: '11px', padding: '2px 6px',
                                                borderRadius: '8px', border: '1px solid #ddd', backgroundColor: '#fff',
                                                maxWidth: '100px', cursor: 'pointer'
                                            }}
                                            title="Cambiar de zona"
                                        >
                                            <option value="">Sin zona</option>
                                            {zonas.map(z => (
                                                <option key={z.id} value={z.id}>{z.nombre}</option>
                                            ))}
                                        </select>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* 🕒 RELOJ FLOTANTE (siempre visible en la pantalla de mesas) */}
            <div style={{
                position: 'fixed', left: '20px', bottom: '20px', zIndex: 9999,
                backgroundColor: '#111', color: '#fff', borderRadius: '16px',
                padding: '10px 18px', boxShadow: '0 8px 24px rgba(0,0,0,0.25)',
                display: 'flex', alignItems: 'center', gap: '10px'
            }}>
                <i className="far fa-clock" style={{ fontSize: '18px', color: '#f97316' }}></i>
                <div style={{ lineHeight: 1.15 }}>
                    <div style={{ fontSize: '20px', fontWeight: 800, letterSpacing: '0.5px' }}>
                        {reloj.toLocaleTimeString('es-CL', { hour12: false })}
                    </div>
                    <div style={{ fontSize: '9px', color: '#999', textTransform: 'uppercase' }}>
                        {reloj.toLocaleDateString('es-CL', { weekday: 'long', day: 'numeric', month: 'short' })}
                    </div>
                </div>
            </div>

            <style>{`
                .mesa-circular:hover {
                    transform: scale(1.1);
                    box-shadow: 0 5px 15px rgba(0,0,0,0.2) !important;
                }
                .mesa-circular:active {
                    transform: scale(0.95);
                }
            `}</style>
        </div>
    );
};

export default PedidosMesa;
